import { lazy, Suspense, ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PermissionProtectedRoute from '../components/PermissionProtectedRoute';
import PageTitle from '../components/PageTitle';

const AdminUsersPage = lazy(() => import('../pages/AdminUsersPage'));
const AdminUserEditPage = lazy(() => import('../pages/admin-user-edit/AdminUserEditPage'));
const AdminDashboardPage = lazy(() => import('../pages/AdminDashboardPage'));
const AdminJobMonitorPage = lazy(() => import('../pages/AdminJobMonitorPage'));
const AdminHealthPage = lazy(() => import('../pages/AdminHealthPage'));
const AdminAuditPage = lazy(() => import('../pages/AdminAuditPage'));
const AdminDocumentReviewsPage = lazy(() => import('../pages/admin-document-review'));
const AdminDocumentRequirementsPage = lazy(() => import('../pages/AdminDocumentRequirementsPage'));
const AdminDocumentTypesPage = lazy(() => import('../pages/admin-document-types'));
const AdminVerificationDashboard = lazy(() => import('../components/VerificationWorkflowDashboard'));
const RejectionTemplateManager = lazy(() => import('../components/RejectionTemplateManager'));
const PricingConfigurationDashboard = lazy(() => import('../pages/pricing-configuration/PricingConfigurationDashboard'));
const AdminCommunicationTemplatesPage = lazy(() => import('../pages/AdminCommunicationTemplatesPage'));
const AdminCampaignsPage = lazy(() => import('../pages/AdminCampaignsPage'));
const AdminCustomerApprovalsPage = lazy(() => import('../pages/AdminCustomerApprovalsPage'));
const AdminCompliancePage = lazy(() => import('../pages/admin/AdminCompliance'));
const AllocatedCompliancePage = lazy(() => import('../pages/admin/AdminCompliance/AllocatedCompliancePage'));
const AdminFinancialDashboardPage = lazy(() => import('../pages/admin-financial/AdminFinancialDashboardPage'));
const AdminDisputesPage = lazy(() => import('../pages/AdminDisputesPage'));
const AdminWarrantyPage = lazy(() => import('../pages/AdminWarrantyPage'));
const AdminWarrantyDefaultsPage = lazy(() => import('../pages/AdminWarrantyDefaultsPage'));
const AdminServiceCasesPage = lazy(() => import('../pages/admin/service-cases/AdminServiceCasesPage'));
const AdminServiceCaseDetailPage = lazy(() => import('../pages/admin/service-cases/AdminServiceCaseDetailPage'));
const AdminTradeTypesPage = lazy(() => import('../pages/AdminTradeTypesPage'));
const AdminCertificateRulesPage = lazy(() => import('../pages/admin/AdminCertificateRulesPage'));
const AdminKnowledgeBasePage = lazy(() => import('../pages/AdminKnowledgeBasePage'));
const AdminExpertManufacturersPage = lazy(() => import('../pages/admin/experts/AdminExpertManufacturersPage'));
const AdminExpertDocumentsPage = lazy(() => import('../pages/admin/experts/AdminExpertDocumentsPage'));
const AdminExpertTranscriptsPage = lazy(() => import('../pages/admin/experts/AdminExpertTranscriptsPage'));
const AdminAiConversationsPage = lazy(() => import('../pages/admin/ai-conversations/AdminAiConversationsPage'));
const AdminExpertMetricsPage = lazy(() => import('../pages/admin/experts/AdminExpertMetricsPage'));
const AdminExpertPilotPage = lazy(() => import('../pages/admin/experts/AdminExpertPilotPage'));
const AdminExpertPartnerQrPage = lazy(() => import('../pages/admin/experts/AdminExpertPartnerQrPage'));
const AdminExpertTestSetsPage = lazy(() => import('../pages/admin/experts/AdminExpertTestSetsPage'));
const AdminExpertFlowsPage = lazy(() => import('../pages/admin/experts/AdminExpertFlowsPage'));
const AdminExpertTipsPage = lazy(() => import('../pages/admin/experts/AdminExpertTipsPage'));
const AdminExpertChatPage = lazy(() => import('../pages/admin/experts/AdminExpertChatPage'));
const AdminNewsPage = lazy(() => import('../pages/AdminNewsPage'));
const AdminReviewModerationPage = lazy(() => import('../pages/AdminReviewModerationPage'));
const AdminPlatformDocumentsPage = lazy(() => import('../pages/AdminPlatformDocumentsPage'));
const AdminAccreditationsPage = lazy(() => import('../pages/AdminAccreditationsPage'));
const AdminDisputeResolutionPage = lazy(() => import('../pages/admin/AdminDisputeResolutionPage'));
const AdminTradeAnalyticsPage = lazy(() => import('../pages/AdminTradeAnalyticsPage'));
const AdminAppRolloutPage = lazy(() => import('../pages/AdminAppRolloutPage'));
const AdminNotificationLogPage = lazy(() => import('../pages/AdminNotificationLogPage'));
const AdminCommunicationLogsPage = lazy(() => import('../pages/admin/AdminCommunicationLogsPage'));
const AdminSmsConversationsPage = lazy(() => import('../pages/admin/AdminSmsConversationsPage'));
const AdminWorkflowTemplatesPage = lazy(() => import('../pages/AdminWorkflowTemplatesPage'));
const AdminVariationPromptsPage = lazy(() => import('../pages/admin/AdminVariationPromptsPage'));
const WorkflowTemplateEditorPage = lazy(() => import('../pages/workflow-templates/WorkflowTemplateEditorPage'));
const PlatformPipelinesPage = lazy(() => import('../pages/admin/PlatformPipelinesPage'));
const DirectJobSettingsPage = lazy(() => import('../pages/admin/DirectJobSettingsPage'));
const PaymentTermsSettingsPage = lazy(() => import('../pages/admin/PaymentTermsSettingsPage'));
const SchedulingSlaSettingsPage = lazy(() => import('../pages/admin/SchedulingSlaSettingsPage'));
const AdminServiceTypesPage = lazy(() => import('../pages/admin/AdminServiceTypesPage'));
const AdminSpecializationsPage = lazy(() => import('../pages/AdminSpecializationsPage'));
const AdminIssueCenter = lazy(() => import('../features/admin-feedback/AdminIssueCenter'));
const AdminIssueDetailView = lazy(() => import('../features/admin-feedback/AdminIssueDetailView'));
const AdminPlatformStaffPage = lazy(() => import('../pages/AdminPlatformStaffPage'));
const AdminRolesPage = lazy(() => import('../pages/AdminRolesPage'));
const AdminBalanceTopUpPage = lazy(() => import('../pages/AdminBalanceTopUpPage'));
const AdminOnSitePaymentsPage = lazy(() => import('../pages/AdminOnSitePaymentsPage'));
const AdminNswLicenceLookupPage = lazy(() => import('../pages/admin-nsw-licence-lookup/index'));
const CancellationPenaltyQueuePage = lazy(() => import('../pages/admin/CancellationPenaltyQueue'));
const AdminVariationsPage = lazy(() => import('../pages/admin/AdminVariationsPage'));
const AdminQuotesPage = lazy(() => import('../pages/admin/AdminQuotesPage'));
const AdminCoverageMapPage = lazy(() => import('../pages/AdminCoverageMapPage'));
const AdminJobMatchingPage = lazy(() => import('../pages/AdminJobMatchingPage'));
const PlatformCrmPage = lazy(() => import('../pages/admin/crm/PlatformCrmPage'));

const AdminCompaniesPage = lazy(() => import('../pages/admin/AdminCompaniesPage'));
const AdminReferralsPage = lazy(() => import('../pages/admin/AdminReferralsPage'));
const AiAnalystPage = lazy(() => import('../pages/admin/ai-analyst/AiAnalystPage'));
const AdminInvoicesPage = lazy(() => import('../pages/admin/AdminInvoicesPage'));
const AdminRemittancesPage = lazy(() => import('../pages/admin/AdminRemittancesPage'));
const AdminCreditsPage = lazy(() => import('../pages/admin/AdminCreditsPage'));
const AdminWorkflowDiagnosticsPage = lazy(() => import('../pages/admin/AdminWorkflowDiagnosticsPage'));
const JobProjectionsDashboard = lazy(() => import('../pages/admin/JobProjectionsDashboard'));

function RouteFallback() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
        </Box>
    );
}

function suspense(element: ReactNode) {
    return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export default function AdminRoutes() {
    return (
        <Routes>
            <Route
                path="/referrals"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Referral Hub">{suspense(<AdminReferralsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/companies"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Company Compliance Hub">{suspense(<AdminCompaniesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/users"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Admin Users">{suspense(<AdminUsersPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/users/:userId"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Edit User">{suspense(<AdminUserEditPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/approvals"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Customer Approvals">{suspense(<AdminCustomerApprovalsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/crm"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Platform CRM">{suspense(<PlatformCrmPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/issues"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Issue Center">{suspense(<AdminIssueCenter />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/issues/:issueId"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Issue Details">{suspense(<AdminIssueDetailView />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/dashboard"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Admin Dashboard">{suspense(<AdminDashboardPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/quotes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Platform Quotes Hub">{suspense(<AdminQuotesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/financial"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Admin Financials">{suspense(<AdminFinancialDashboardPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/job-projections"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Cash Flow Forecast">{suspense(<JobProjectionsDashboard />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/invoices"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Admin Invoices">{suspense(<AdminInvoicesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/remittances"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Remittance History">{suspense(<AdminRemittancesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/credits"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Admin Credits">{suspense(<AdminCreditsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/on-site-payments"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="On-Site Payments">{suspense(<AdminOnSitePaymentsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/job-monitor"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Job Monitor">{suspense(<AdminJobMonitorPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/workflow-diagnostics"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Workflow Diagnostics Console">{suspense(<AdminWorkflowDiagnosticsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/disputes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Disputes">{suspense(<AdminDisputesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/service-cases"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Service Cases">{suspense(<AdminServiceCasesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/service-cases/:id"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Service Case">{suspense(<AdminServiceCaseDetailPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/warranty"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Warranty Claims">{suspense(<AdminWarrantyPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/warranty"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Warranty Defaults">{suspense(<AdminWarrantyDefaultsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/health"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="System Health">{suspense(<AdminHealthPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/audit"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Audit Logs">{suspense(<AdminAuditPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/trade-analytics"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Trade Analytics">{suspense(<AdminTradeAnalyticsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/app-rollout"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="App rollout">{suspense(<AdminAppRolloutPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/notification-log"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Notification Log">{suspense(<AdminNotificationLogPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/communication-logs"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Communication Logs">{suspense(<AdminCommunicationLogsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/sms-conversations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="SMS Conversations">{suspense(<AdminSmsConversationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/documents/review"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Document Review Queue">{suspense(<AdminDocumentReviewsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/document-requirements"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Document Requirements">{suspense(<AdminDocumentRequirementsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/rejection-templates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Rejection Templates">{suspense(<RejectionTemplateManager />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/verification-dashboard"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Verification Dashboard">{suspense(<AdminVerificationDashboard />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/pricing"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Pricing Configuration">{suspense(<PricingConfigurationDashboard />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/communication-templates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Communication Templates">{suspense(<AdminCommunicationTemplatesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/campaigns"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all', 'crm:manage']}>
                        <PageTitle title="Campaigns">{suspense(<AdminCampaignsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/document-types"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Document Types">{suspense(<AdminDocumentTypesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/compliance"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Compliance Dashboard">{suspense(<AdminCompliancePage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/compliance/allocated"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Allocated Compliance Audit">{suspense(<AllocatedCompliancePage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/knowledge-base"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Knowledge Base">{suspense(<AdminKnowledgeBasePage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/manufacturers"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Manufacturers">{suspense(<AdminExpertManufacturersPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/documents"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Knowledge">{suspense(<AdminExpertDocumentsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/transcripts"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Transcripts">{suspense(<AdminExpertTranscriptsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/ai-conversations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="AI Conversations">{suspense(<AdminAiConversationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/metrics"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Metrics">{suspense(<AdminExpertMetricsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/pilot"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Pilot">{suspense(<AdminExpertPilotPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/partner-qr"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Partner QR">{suspense(<AdminExpertPartnerQrPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/test-sets"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Test Sets">{suspense(<AdminExpertTestSetsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/flows"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Expert Flows">{suspense(<AdminExpertFlowsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/tips"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Field Tips">{suspense(<AdminExpertTipsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/experts/chat"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Technical Expert">{suspense(<AdminExpertChatPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/news"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Industry News">{suspense(<AdminNewsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/trade-types"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Trade Types">{suspense(<AdminTradeTypesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/certificate-rules"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Certificate Rules">{suspense(<AdminCertificateRulesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/accreditations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Accreditations">{suspense(<AdminAccreditationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/specializations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Specializations">{suspense(<AdminSpecializationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            {/* Fallback for /admin/documents to review page as in original App.tsx line 129??
           Actually line 129 was: <Route path="/admin/documents" element={suspense(<AdminDocumentReviewPage />)} />
           Wait, there is also line 317: <Route path="/admin/documents/review" ... />
           I will keep existing paths.
           The path "/documents" in AdminRoutes will match "/admin/documents" in AppRoutes.
       */}
            <Route
                path="/documents"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Document Review">{suspense(<AdminDocumentReviewsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/reviews"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Review Moderation">{suspense(<AdminReviewModerationPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/platform-documents"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Platform Documents">{suspense(<AdminPlatformDocumentsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/variation-disputes"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Variation Disputes">{suspense(<AdminDisputeResolutionPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/variations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="All Variations">{suspense(<AdminVariationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/cancellation-penalties"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Cancellation Penalties">{suspense(<CancellationPenaltyQueuePage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/workflow-templates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Workflow Templates">{suspense(<AdminWorkflowTemplatesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/integration-templates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Platform Integration Templates">{suspense(<PlatformPipelinesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/workflow-templates/new"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="New Workflow Template">{suspense(<WorkflowTemplateEditorPage mode="platform" />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/workflow-templates/edit/:id"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Edit Workflow Template">{suspense(<WorkflowTemplateEditorPage mode="platform" />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/direct-job-settings"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Direct Job Settings">{suspense(<DirectJobSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/payment-terms"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Payment terms">{suspense(<PaymentTermsSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/scheduling-sla"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Scheduling SLA Hub">{suspense(<SchedulingSlaSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/variation-prompts"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Site Variation Detection Prompts">{suspense(<AdminVariationPromptsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/service-types"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Service Types">{suspense(<AdminServiceTypesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/platform-staff"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Platform Staff">{suspense(<AdminPlatformStaffPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/roles"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Roles & Permissions">{suspense(<AdminRolesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/balance-topup"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Balance Top Up">{suspense(<AdminBalanceTopUpPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/nsw-licence-lookup"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="NSW Licence Lookup">{suspense(<AdminNswLicenceLookupPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/coverage-map"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Coverage Map">{suspense(<AdminCoverageMapPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/job-matching"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Job Matching">{suspense(<AdminJobMatchingPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/ai-analyst"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="AI Analyst">{suspense(<AiAnalystPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
    );
}
